import { useEffect, useState } from "react";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PERMISSIONS } from "../lib/permissions";

const empty = {
  nombre: "",
  nif: "",
  creditor_id: "",
  iban: "",
  bic: "",
  direccion: "",
  codigo_postal: "",
  ciudad: "",
  provincia: "",
};

/**
 * Formulario reutilizable de configuración de la falla (identidad + SEPA + dirección).
 * Diseñado para inyectarse en tabs / paneles.
 *
 * El acceso a este componente debe ser gateado en el contenedor por
 * `manage_organization_config`. El propio formulario aplica `disabled` y un
 * banner read-only si el usuario no tiene permiso, por defensa en profundidad.
 */
export default function ConfigForm() {
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.MANAGE_ORGANIZATION_CONFIG);
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get("/falla-config");
        setData({ ...empty, ...res.data });
      } catch (err) {
        toast.error("No se pudo cargar la configuración");
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const onChange = (k) => (e) =>
    setData((d) => ({ ...d, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await api.put("/falla-config", data);
      setData({ ...empty, ...res.data });
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-med-muted py-8" data-testid="config-loading">
        Cargando configuración…
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-med-surface border border-med-border rounded-2xl p-6 md:p-8 max-w-3xl"
      data-testid="config-form"
    >
      {!canEdit && (
        <div
          className="mb-6 rounded-xl border border-med-warning/30 bg-med-warning/10 text-med-warning px-4 py-3 text-sm"
          data-testid="config-readonly-banner"
        >
          Solo usuarios con permiso para gestionar la configuración pueden
          modificar estos datos.
        </div>
      )}

      <h2 className="font-display text-xl font-bold text-med-text mb-1">
        Identidad
      </h2>
      <p className="text-sm text-med-muted mb-5">
        Aparece en recibos y como ordenante en la remesa.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Field
          label="Nombre de la falla *"
          value={data.nombre}
          onChange={onChange("nombre")}
          testid="config-nombre"
          disabled={!canEdit}
          required
        />
        <Field
          label="NIF / CIF *"
          value={data.nif}
          onChange={onChange("nif")}
          testid="config-nif"
          disabled={!canEdit}
          placeholder="G12345678"
          required
        />
      </div>

      <h2 className="font-display text-xl font-bold text-med-text mb-1">
        SEPA / Banco
      </h2>
      <p className="text-sm text-med-muted mb-5">
        El ID acreedor SEPA lo proporciona tu banco. Suele empezar por{" "}
        <span className="font-mono">ES</span>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Field
          label="ID acreedor SEPA *"
          value={data.creditor_id}
          onChange={onChange("creditor_id")}
          testid="config-creditor"
          disabled={!canEdit}
          placeholder="ES12ZZZG12345678"
          required
          mono
        />
        <Field
          label="BIC *"
          value={data.bic}
          onChange={onChange("bic")}
          testid="config-bic"
          disabled={!canEdit}
          placeholder="CAIXESBBXXX"
          required
          mono
        />
        <div className="md:col-span-2">
          <Field
            label="IBAN *"
            value={data.iban}
            onChange={onChange("iban")}
            testid="config-iban"
            disabled={!canEdit}
            placeholder="ES00 0000 0000 00 0000000000"
            required
            mono
          />
        </div>
      </div>

      <h2 className="font-display text-xl font-bold text-med-text mb-1">
        Dirección
      </h2>
      <p className="text-sm text-med-muted mb-5">
        Opcional. Se mostrará en los recibos.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Field
            label="Dirección"
            value={data.direccion}
            onChange={onChange("direccion")}
            testid="config-direccion"
            disabled={!canEdit}
            placeholder="C/ Falla, 1"
          />
        </div>
        <Field
          label="Código postal"
          value={data.codigo_postal}
          onChange={onChange("codigo_postal")}
          testid="config-cp"
          disabled={!canEdit}
          placeholder="46001"
        />
        <Field
          label="Ciudad"
          value={data.ciudad}
          onChange={onChange("ciudad")}
          testid="config-ciudad"
          disabled={!canEdit}
          placeholder="València"
        />
        <div className="md:col-span-2">
          <Field
            label="Provincia"
            value={data.provincia}
            onChange={onChange("provincia")}
            testid="config-provincia"
            disabled={!canEdit}
            placeholder="València"
          />
        </div>
      </div>

      <div className="flex justify-end mt-8 pt-6 border-t border-med-border">
        <button
          type="submit"
          disabled={!canEdit || saving}
          data-testid="save-config-btn"
          className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98] transition-colors"
        >
          <FloppyDisk size={18} weight="bold" />
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  testid,
  disabled,
  placeholder,
  required,
  mono,
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
        {label}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className={`w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-colors disabled:bg-med-surface2 disabled:cursor-not-allowed ${
          mono ? "font-mono" : ""
        }`}
      />
    </div>
  );
}
